import React, { memo, useCallback, useMemo } from 'react';

import { Box, ButtonBase, Typography } from '@mui/material';
import AccordionCDS from 'app/shared/componentsRestyled/coreDesignSystem/AccordionCDS';
import { InfoIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import { LabelSize } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PopTipCDS from 'app/shared/componentsRestyled/coreDesignSystem/PopTipCDS';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import useEntityReferenceIndex from '../hooks/useEntityReferenceIndex';
import {
  ENTITY_REFERENCE_ICON_BY_TYPE,
  getEntityReferenceKey,
} from '../TeamChat.entityReferences.utils';

import {
  accordionStyles,
  activeFilterRowStyles,
  countQualifierIconStyles,
  getItemRowStyles,
  groupLabelStyles,
  groupsStyles,
  groupStyles,
  itemLabelStyles,
  rootStyles,
} from './TeamChatReferencesRail.styles';
import { TeamChatReferencesRailTestIds } from './TeamChatReferencesRail.testIds';
import {
  buildReferenceGroups,
  findReferenceItemByKey,
  getReferenceCountLabel,
  getTotalReferenceItemCount,
  REFERENCE_COUNT_QUALIFIER,
} from './TeamChatReferencesRail.utils';

// Portal the popup out of the accordion summary, whose click-to-expand handler would otherwise
// intercept a click meant for the tooltip trigger.
const countQualifierPopperProps = { disablePortal: false };

// Renders NOTHING when the channel has never referenced anything — no empty "References" header on
// a channel that has never used the feature (DEV-5448).
const TeamChatReferencesRail = memo(
  ({ chatId = null, activeEntityKey = null, onSelectEntity, onClear }) => {
    const index = useEntityReferenceIndex(chatId);
    const groups = useMemo(() => buildReferenceGroups(index), [index]);
    const totalCount = useMemo(() => getTotalReferenceItemCount(groups), [groups]);
    const activeItem = useMemo(
      () => findReferenceItemByKey(groups, activeEntityKey),
      [groups, activeEntityKey],
    );
    const totalLabelProps = useMemo(
      () => ({ title: getReferenceCountLabel(totalCount), size: LabelSize.S }),
      [totalCount],
    );
    // The count is an approximation (client-side index over the loaded message page only, ahead
    // of DEV-5447's server aggregate) — surfaced as a fixed, non-props-dependent qualifier node so
    // it memoizes for the accordion's `additionalSummaryContent` slot.
    const countQualifierContent = useMemo(
      () => (
        <PopTipCDS
          PopperProps={countQualifierPopperProps}
          placement="top"
          title={REFERENCE_COUNT_QUALIFIER}
        >
          <Box component="span" sx={countQualifierIconStyles}>
            <InfoIcon size={IconSize[14]} />
          </Box>
        </PopTipCDS>
      ),
      [],
    );
    const onItemClick = useCallback(item => () => onSelectEntity(item), [onSelectEntity]);

    if (!groups.length) {
      return null;
    }

    return (
      <Box data-testid={TeamChatReferencesRailTestIds.ROOT} sx={rootStyles}>
        {!!activeItem && (
          <Box sx={activeFilterRowStyles}>
            <Typography data-truncated variant="regular12">
              {`Filtered by ${activeItem.label}`}
            </Typography>
            <ButtonBase
              disableRipple
              data-testid={TeamChatReferencesRailTestIds.CLEAR_BUTTON}
              onClick={onClear}
            >
              <Typography color="primary.main" variant="medium12">
                Clear
              </Typography>
            </ButtonBase>
          </Box>
        )}
        <AccordionCDS
          defaultExpanded
          additionalSummaryContent={countQualifierContent}
          className="p-12"
          customStyles={accordionStyles}
          labelProps={totalLabelProps}
          title="References"
          titleVariant="medium14"
        >
          <Box sx={groupsStyles}>
            {groups.map(group => (
              <Box key={group.type} sx={groupStyles}>
                <Typography color="text.secondary" sx={groupLabelStyles} variant="medium12">
                  {group.label}
                </Typography>
                {group.items.map(item => {
                  const key = getEntityReferenceKey(item);
                  const Icon = ENTITY_REFERENCE_ICON_BY_TYPE[item.entityType];

                  return (
                    <ButtonBase
                      disableRipple
                      data-testid={TeamChatReferencesRailTestIds.ITEM}
                      key={key}
                      sx={getItemRowStyles(key === activeEntityKey)}
                      onClick={onItemClick(item)}
                    >
                      <Icon size={IconSize[14]} />
                      <Typography data-truncated sx={itemLabelStyles} variant="regular12">
                        {item.label}
                      </Typography>
                      <LabelCDS size={LabelSize.S} title={String(item.count)} />
                    </ButtonBase>
                  );
                })}
              </Box>
            ))}
          </Box>
        </AccordionCDS>
      </Box>
    );
  },
);
TeamChatReferencesRail.propTypes = {
  onSelectEntity: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  chatId: PropTypes.string,
  activeEntityKey: PropTypes.string,
};

export default TeamChatReferencesRail;
